import { isBefore } from 'date-fns'

import { isTaxFiler } from '#dictionary/filingStatuses'

import type { StandardDeductionGroup, StandardDeductionMemberGroup } from '.'

// Cutoff date used by the IRS for the "age 65+" additional standard
// deduction: qualifying birthday must be strictly before Jan 2, 1960
// (i.e. the person turned 65 by Jan 1 of the relevant tax year). Stable
// law, encoded here rather than injected.
//
// TODO(ZL-976): The cutoff is currently hard-coded to a fixed 1960-01-02
// date, so the "65+" qualifier does not roll forward with future tax
// years. In projection years, anyone born after 1960-01-02 will never
// qualify as senior even once they actually turn 65. Fix by deriving the
// cutoff from the tax year (Jan 2 of `year - 65`) and threading `year`
// through `getStandardDeductionGroup`. See ZL-976 for STR, reasoning,
// and impact assessment.
const SENIOR_DEDUCTION_CUTOFF_DATE = new Date('1960-01-02')

// Minimal structural shape of a household member for deduction purposes.
// The standardDeductions layer stays decoupled from the profile classes —
// any object matching this shape works (ProfileService / MemberService
// satisfy it structurally today; future pure-function refactors will keep
// working as long as the fields exist).
export interface StandardDeductionMember {
  kind: 'person' | 'partner' | 'child' | 'dependent'
  birthday: Date
  isBlind: boolean
}

export interface StandardDeductionProfile {
  person: StandardDeductionMember
  partner: StandardDeductionMember | null | undefined
}

// Household deduction group = sum of each qualifying member's contributions:
// - Only `person` and `partner` contribute (children / dependents don't
//   file jointly for standard-deduction purposes).
// - Each contribution is 0..2: one point for being blind, one for age 65+.
// - Household total is bounded 0..4 (both spouses blind + 65+).
export const getStandardDeductionGroup = (
  profile: StandardDeductionProfile
): StandardDeductionGroup => {
  const personGroup = memberGroup(profile.person)
  const partnerGroup = profile.partner ? memberGroup(profile.partner) : 0
  return (personGroup + partnerGroup) as StandardDeductionGroup
}

const memberGroup = (
  member: StandardDeductionMember
): StandardDeductionMemberGroup => {
  if (!isTaxFiler(member)) return 0
  const isSenior = isBefore(member.birthday, SENIOR_DEDUCTION_CUTOFF_DATE)
  return (Number(member.isBlind) +
    Number(isSenior)) as StandardDeductionMemberGroup
}
